import { BookingScheduleTime } from "@/lib/types/schedule-types";
import { TimeData } from "@/lib/utils/time-utils";
import { TimeMap } from "~/components/listings/data/time-data";
import { ListingData } from "~/context/ListingPageDataContext";
import { IMonthAvailability, ITimeAvailability } from "~/types/api-types";

type MinimumHoursListing = Partial<
  Pick<ListingData, "useType" | "listingMinimumDuration">
>;

/** Splits a month of availability into fully and partially booked dates. */
const sortMonthAvailability = (
  monthAvailability: Record<string, IMonthAvailability>,
) => {
  const fullyBooked = [];
  const partiallyBooked = [];

  for (const day in monthAvailability) {
    const dayHash = monthAvailability[day];
    if (dayHash.is_fully_booked) {
      const { year, month } = dayHash;
      fullyBooked.push(new Date(year, month - 1, +day));
    }
    if (dayHash.is_partially_booked) {
      const { year, month } = dayHash;
      partiallyBooked.push(new Date(year, month - 1, +day));
    }
  }

  return { fullyBooked, partiallyBooked };
};

/** Maps API time availability onto schedule picker time options. */
const mapTimeAvailabilityToTimeOptions = (
  timeAvailability: ITimeAvailability[],
): BookingScheduleTime[] => {
  return timeAvailability.map((availability) => ({
    ...TimeMap[availability.time_index as keyof typeof TimeMap],
    available: availability.available,
  }));
};

/**
 * Time options when availability can't be fetched: every non-overnight slot as a
 * start, every slot strictly after the start as an end. Same rules as the design
 * system's generateTimeOptions, so failing open never offers an inverted window.
 */
const getFallbackTimeOptions = (
  startTimeIndex?: number,
): BookingScheduleTime[] => {
  if (startTimeIndex === undefined) {
    return TimeData.filter((time) => !time.overnight);
  }

  return TimeData.filter(
    (time) => time.time_index !== undefined && time.time_index > startTimeIndex,
  );
};

/** Minimum booking hours are enforced for Event listings only. */
const getEnforceMinimumHours = (listing: MinimumHoursListing) => {
  return listing.useType === "Event";
};

/** Minimum duration the schedule picker auto-corrects to; 0 when unset or non-numeric. */
const getMinDuration = (listing: MinimumHoursListing) => {
  if (!getEnforceMinimumHours(listing)) {
    return 0;
  }

  const minDuration = parseFloat(listing.listingMinimumDuration ?? "0");
  return Number.isNaN(minDuration) ? 0 : minDuration;
};

export {
  sortMonthAvailability,
  mapTimeAvailabilityToTimeOptions,
  getFallbackTimeOptions,
  getEnforceMinimumHours,
  getMinDuration,
};
